import { signal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ErrorBanner } from '../../components/ErrorBanner'
import { Loading } from '../../components/Loading'
import { Modal } from '../../components/Modal'
import { useAuth } from '../../hooks/useAuth'
import { styled } from '../../setup'
import type { GatedActionRequest } from '../../store/auth'
import { borders, colors, spacing, typography } from '../../theme'

const Title = styled('h1')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize3xl};
  font-weight: ${typography.fontWeightBold};
  margin: 0 0 ${spacing['2xl']} 0;
`

const RequestList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`

const RequestCard = styled('div')`
  background: ${colors.bgSurface};
  border: ${borders.widthMedium} solid ${colors.border};
  border-radius: ${borders.radiusLg};
  padding: ${spacing.lg};
`

const RequestHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.md};
  margin-bottom: ${spacing.md};
`

const RequestInfo = styled('div')`
  flex: 1;
`

const RequestType = styled('div')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeLg};
  font-weight: ${typography.fontWeightMedium};
  margin-bottom: ${spacing.xs};
`

const RequestChild = styled('div')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
`

const RequestMeta = styled('div')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  margin-bottom: ${spacing.md};
`

const RequestActions = styled('div')`
  display: flex;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`

const Badge = styled('span')<{ variant: 'pending' | 'approved' | 'denied' }>`
  display: inline-block;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borders.radius};
  font-size: 12px;
  font-weight: ${typography.fontWeightMedium};
  text-transform: uppercase;
  letter-spacing: 0.03em;

  background: ${(props) =>
    ({
      pending: 'rgba(251, 191, 36, 0.15)',
      approved: colors.successBg,
      denied: colors.errorBg,
    })[props.variant]};

  color: ${(props) =>
    ({
      pending: '#b45309',
      approved: colors.success,
      denied: colors.error,
    })[props.variant]};
`

const EmptyState = styled('div')`
  text-align: center;
  padding: ${spacing['4xl']} ${spacing['2xl']};
`

const EmptyTitle = styled('h2')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeXl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.md} 0;
`

const EmptyText = styled('p')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeBase};
  margin: 0;
  line-height: 1.6;
`

const ModalContent = styled('div')`
  text-align: center;
`

const ModalText = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};
  line-height: 1.6;
  margin: 0 0 ${spacing['2xl']} 0;
`

const ModalButtons = styled('div')`
  display: flex;
  gap: ${spacing.md};
  justify-content: center;
`

// Local state
const isLoading = signal(true)
const processingId = signal<string | null>(null)
const confirmModal = signal<{ type: 'approve' | 'deny'; request: GatedActionRequest } | null>(null)

function formatRequestType(type: string): string {
  switch (type) {
    case 'ADD_FRIEND':
      return 'Friend Request'
    case 'ENABLE_CHAT':
      return 'Enable Chat'
    case 'PURCHASE':
      return 'Purchase Request'
    default:
      return type.replace(/_/g, ' ')
  }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function RequestsPage() {
  const {
    guardianRequests,
    fetchGuardianRequests,
    approveRequest,
    denyRequest,
    error,
    clearAuthError,
  } = useAuth()

  // Fetch requests on mount
  useEffect(() => {
    const load = async () => {
      isLoading.value = true
      try {
        await fetchGuardianRequests()
      } finally {
        isLoading.value = false
      }
    }
    load()
  }, [])

  const handleApprove = async (request: GatedActionRequest) => {
    processingId.value = request.id
    clearAuthError()

    try {
      await approveRequest(request.id)
      confirmModal.value = null
    } finally {
      processingId.value = null
    }
  }

  const handleDeny = async (request: GatedActionRequest) => {
    processingId.value = request.id
    clearAuthError()

    try {
      await denyRequest(request.id)
      confirmModal.value = null
    } finally {
      processingId.value = null
    }
  }

  const pendingRequests = guardianRequests.value.filter((r) => r.status === 'PENDING')

  if (isLoading.value) {
    return <Loading fullScreen={false} message="Loading requests..." />
  }

  return (
    <>
      <Title>Pending Requests</Title>

      {error.value && <ErrorBanner message={error.value} onDismiss={clearAuthError} />}

      {pendingRequests.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyTitle>No pending requests</EmptyTitle>
            <EmptyText>
              When your children request things like adding friends or enabling features, those
              requests will appear here for your approval.
            </EmptyText>
          </EmptyState>
        </Card>
      ) : (
        <RequestList>
          {pendingRequests.map((request) => (
            <RequestCard key={request.id}>
              <RequestHeader>
                <RequestInfo>
                  <RequestType>{formatRequestType(request.requestType)}</RequestType>
                  <RequestChild>From: {request.childDisplayName}</RequestChild>
                </RequestInfo>
                <Badge variant="pending">Pending</Badge>
              </RequestHeader>

              <RequestMeta>
                Requested {formatDate(request.createdAt)}
                {request.approvalCount > 0 &&
                  ` (${request.approvalCount} approval${request.approvalCount > 1 ? 's' : ''})`}
              </RequestMeta>

              <RequestActions>
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => (confirmModal.value = { type: 'approve', request })}
                  disabled={processingId.value === request.id}
                  loading={processingId.value === request.id}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => (confirmModal.value = { type: 'deny', request })}
                  disabled={processingId.value === request.id}
                >
                  Deny
                </Button>
              </RequestActions>
            </RequestCard>
          ))}
        </RequestList>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal.value !== null}
        onClose={() => (confirmModal.value = null)}
        title={confirmModal.value?.type === 'approve' ? 'Approve Request' : 'Deny Request'}
        maxWidth="400px"
      >
        <ModalContent>
          <ModalText>
            {confirmModal.value?.type === 'approve'
              ? `Are you sure you want to approve this ${formatRequestType(confirmModal.value.request.requestType).toLowerCase()} from ${confirmModal.value.request.childDisplayName}?`
              : `Are you sure you want to deny this ${formatRequestType(confirmModal.value?.request?.requestType || '').toLowerCase()} from ${confirmModal.value?.request?.childDisplayName}?`}
          </ModalText>
          <ModalButtons>
            <Button variant="secondary" onClick={() => (confirmModal.value = null)}>
              Cancel
            </Button>
            <Button
              variant={confirmModal.value?.type === 'approve' ? 'primary' : 'danger'}
              onClick={() => {
                if (confirmModal.value?.type === 'approve') {
                  handleApprove(confirmModal.value.request)
                } else if (confirmModal.value) {
                  handleDeny(confirmModal.value.request)
                }
              }}
              loading={processingId.value !== null}
            >
              {confirmModal.value?.type === 'approve' ? 'Approve' : 'Deny'}
            </Button>
          </ModalButtons>
        </ModalContent>
      </Modal>
    </>
  )
}
